import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import { Marketplace } from "@components/new/Marketplace";
import { Title3, SubTitle3 } from "@components/new/Typo";
import { Table, TableCellLink, TableColumn } from "@components/new/Table";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import { Loader } from "@components/new/Loader";
// import { Button, ButtonSize, ButtonType } from "@components/new/Button";
// import { TabsList, TabsContent } from "@components/new/Tabs/Tabs";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import Photo from "@components/new/Photo";
import { Tooltip } from '@components/new/Tooltip';
import {
  isYmItem,
  isWbItem,
  isAliItem,
  TYmItemSearch,
  TWbItemSearch,
  TAliItemSearch,
  TProductDimensionLimits,
} from "@api/apiClient/fbp";
import {
  getDimensionsErrorHint,
  isDimensionsError,
} from "@utils/fbpDimensions";
import NoItemsIcon from "@assets/icons/hugeicons/package-remove.svg?react";
import SolidInfoIcon from "@assets/icons/solid/circle-info.svg?react";
import styles from './Form.module.scss';

export interface FormType {
  wbId: string;
  wbSellerId: string;
  ymSellerId: string;
}
410
type TRow = (TYmItemSearch | TWbItemSearch | TAliItemSearch);

export const SearchMenuComponent = ({ items, onClickItem, isLoading, isSimple, wbDimensionLimits, aliDimensionLimits, ymDimensionLimits, wbDensityLimits, aliDensityLimits, ymDensityLimits }: {
  items: TRow[];
  onClickItem: (v: TRow[]) => void;
  isLoading?: boolean;
  isSimple?: boolean;
  wbDimensionLimits?: TProductDimensionLimits;
  aliDimensionLimits?: TProductDimensionLimits;
  ymDimensionLimits?: TProductDimensionLimits;
  wbDensityLimits?: number;
  aliDensityLimits?: number;
  ymDensityLimits?: number;
}) => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();
  const [itemsFiltered, setItemsFiltered] = useState<TRow[]>([]);

  useEffect(() => {
    setItemsFiltered(items);
  }, [items])

  // Товар нельзя добавить в заявку: запрещённый или не прошедший проверку габаритов
  const isForbiddenRow = (row: TRow) =>
    Boolean(row.isSuspicious) || isDimensionsError(row.errorType);

  const getRowTooltip = (row: TRow): string => {
    if (row.isSuspicious) return t('fbpPage.createOrder.forbiddenRowTooltip');
    return getDimensionsErrorHint(row, wbDimensionLimits, aliDimensionLimits, ymDimensionLimits, t);
  };

  const searchItemsColumns: TableColumn<TRow>[] = useMemo(() => [
    {
      header: t("fbpPage.createOrder.items.photo"),
      renderCell: (row) => (
        <Photo src={row.data.primaryImage} />
      ),
      width: "80px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.createOrder.items.marketplace"),
      renderCell: (row) => (
        <Marketplace name={row.data.marketplace} size="small" />
      ),
      alignment: "center",
      width: "40px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.createOrder.items.name"),
      renderCell: (row) => {
        const description = isYmItem(row.data) ? row.data.sku : (
          isWbItem(row.data) ? row.data.wbNmId : (
            isAliItem(row.data) ? row.data.skuCode : ""
          )
        );
        const density = Math.round((row.data.density?.value ?? 0) * 100) / 100;
        const recommendedDensity = (isWbItem(row.data) ? wbDensityLimits : (
          isAliItem(row.data) ? aliDensityLimits : ymDensityLimits)) || 0;
        return (
          <TableCellLink
            title={row.data.name}
            description={description}
            thirdLine={
              (density && recommendedDensity && (density < recommendedDensity)) ? (
                <SubTitle3 className={styles.lowDensityWarning}>
                  {t("fbpPage.createOrder.lowDensity")}
                  <Tooltip
                    action={
                      <IconButton
                        icon={SolidInfoIcon}
                        onClick={() => { }}
                        size={IconButtonSize.L}
                        type={IconButtonType.ICON}
                      />
                    }
                    position="top"
                    text={t(`fbpPage.createOrder.lowDensityTooltip`, { density, recommendedDensity })}
                    whiteSpace="normal"
                    maxWidth={320}
                  />
                </SubTitle3>
              ) : null}
            to={row.data.url}
            external
            maxWidth="680px"
          />
        )
      },
      width: "700px", // todo
    },
    {
      header: t("fbpPage.createOrder.items.size"),
      valueGetter: (row) => ((isWbItem(row) && row.wbTechSize) || ""),
      width: "50px",
      fixWidth: true,
      alignment: "center",
    },
  ], [currentLanguage, itemsFiltered]);

  if (isLoading) {
    return (
      <div className={styles.searchMenuEmpty}>
        <Loader className={styles.loader} />
      </div>
    )
  }

  if (!itemsFiltered.length) {
    return isSimple ? null : (
      <div className={styles.searchMenuEmpty}>
        <MessageModalContent
          title={t('fbpPage.createOrder.searchDropdown.noItemsTitle')}
          description={t('fbpPage.createOrder.searchDropdown.noItemsDescription')}
          variant="info"
          Icon={<NoItemsIcon />}
          iconSize="l"
        />
      </div>
    )
  }

  return (
    <div className={styles.searchMenu}>
      {isSimple ? null : (
        <div className={styles.searchMenuHeader}>
          <Title3 color="grey-800">{t('fbpPage.createOrder.searchDropdown.someItems')}</Title3>
          <LinkButton
            size={LinkButtonSize.M}
            title={t('fbpPage.createOrder.searchDropdown.selectAll')}
            onClick={() => onClickItem(itemsFiltered.filter(v => !isForbiddenRow(v)))}
          />
        </div>
      )}
      <Table<TRow>
        isHeadless
        columns={searchItemsColumns}
        data={itemsFiltered}
        rowClassName={(row) => (isForbiddenRow(row.data) ? styles.forbiddenRow : "")}
        rowTooltip={getRowTooltip}
        rowOnClick={(row) => {
          if (isForbiddenRow(row)) return;
          setItemsFiltered(itemsFiltered.filter(v => (v.id !== row.id)));
          onClickItem([row]);
        }}
      />
    </div>
  )
};

export const SearchMenuComponentSimple = (props: {
  items: TRow[];
  onClickItem: (v: TRow[]) => void;
  isLoading?: boolean;
}) => (
  <SearchMenuComponent {...props} isSimple />
)
